import {
  type SponsoredDisplayAudienceTarget,
  SponsoredDisplayAudienceTargetSchema,
  type SponsoredDisplayContextualTarget,
  SponsoredDisplayContextualTargetSchema,
  type SponsoredDisplayOptimizationGoal,
} from "@adpilot/shared";
import { format } from "date-fns";
import { z } from "zod";
import type { SelectedProduct } from "@/lib/campaign-creation-state";

export type TargetingMode = "audience" | "contextual";
export type AudienceTab = "amazon" | "views" | "purchases" | "amc";
export type ContextualTab = "categories" | "products";
export type BidMode = "suggested" | "custom";

export const CREATIVE_PREVIEW_PLACEMENTS = [
  "product_page_right",
  "product_page_middle",
  "amazon_mobile_rectangle",
  "mobile_leaderboard",
  "wide_skyscraper",
  "medium_rectangle",
  "leaderboard",
  "billboard",
  "large_rectangle",
  "amazon_top_stripe",
] as const;
export const CREATIVE_PREVIEW_PAGE_COUNT = 10;

export type CreativePreviewPlacement = (typeof CREATIVE_PREVIEW_PLACEMENTS)[number];

export interface TargetRow<T> {
  id: string;
  label: string;
  target: T;
}

export type AnyTargetRow = TargetRow<
  SponsoredDisplayAudienceTarget | SponsoredDisplayContextualTarget
>;

export interface CreativeDraft {
  expanded: boolean;
  placement: CreativePreviewPlacement;
  previewPage: number;
  logoEnabled: boolean;
  headlineEnabled: boolean;
  imageEnabled: boolean;
  headline: string;
  logoAssetId: string | null;
  imageAssetId: string | null;
}

export interface SponsoredDisplayDraft {
  campaignName: string;
  portfolioId: string | null;
  startDate: string;
  endDate: string;
  dailyBudget: string;
  adGroupName: string;
  optimizationGoal: SponsoredDisplayOptimizationGoal;
  targetingMode: TargetingMode;
  audienceTab: AudienceTab;
  contextualTab: ContextualTab;
  bidMode: BidMode;
  customBid: string;
  products: SelectedProduct[];
  productEnterInput: string;
  audienceTargets: Array<TargetRow<SponsoredDisplayAudienceTarget>>;
  contextualTargets: Array<TargetRow<SponsoredDisplayContextualTarget>>;
  contextualAsinInput: string;
  audienceSearch: string;
  creative: CreativeDraft;
}

/** Field-level setter shared by every section that edits the top-level draft. */
export type UpdateDraftField = <K extends keyof SponsoredDisplayDraft>(
  key: K,
  value: SponsoredDisplayDraft[K],
) => void;

const SelectedProductDraftSchema = z
  .object({
    // Drafts saved before #673 carry only `sku`; the seller's SKU is its listing key.
    listingKey: z.string().min(1).optional(),
    sku: z.string().min(1).nullable(),
    asin: z.string().regex(/^[A-Z0-9]{10}$/),
    variationGroupKey: z.string(),
    title: z.string().nullable(),
    imageUrl: z.string().nullable(),
  })
  .transform((product) => ({
    ...product,
    listingKey: product.listingKey ?? product.sku ?? product.asin,
  }));

const TargetRowSchema = <T extends z.ZodTypeAny>(schema: T) =>
  z.object({ id: z.string(), label: z.string(), target: schema });

const SponsoredDisplayDraftSchema = z.object({
  campaignName: z.string(),
  portfolioId: z.string().nullable(),
  startDate: z.string(),
  endDate: z.string(),
  dailyBudget: z.string(),
  adGroupName: z.string(),
  optimizationGoal: z.enum(["page_visits", "conversions", "viewable_impressions"]),
  targetingMode: z.enum(["audience", "contextual"]),
  audienceTab: z.enum(["amazon", "views", "purchases", "amc"]),
  contextualTab: z.enum(["categories", "products"]),
  bidMode: z.enum(["suggested", "custom"]),
  customBid: z.string(),
  products: z.array(SelectedProductDraftSchema).max(500),
  productEnterInput: z.string(),
  audienceTargets: z.array(TargetRowSchema(SponsoredDisplayAudienceTargetSchema)),
  contextualTargets: z.array(TargetRowSchema(SponsoredDisplayContextualTargetSchema)),
  contextualAsinInput: z.string(),
  audienceSearch: z.string(),
  creative: z.object({
    expanded: z.boolean(),
    placement: z.enum(CREATIVE_PREVIEW_PLACEMENTS).default("product_page_right"),
    previewPage: z.number().int().min(1).max(CREATIVE_PREVIEW_PAGE_COUNT).default(1),
    logoEnabled: z.boolean(),
    headlineEnabled: z.boolean(),
    imageEnabled: z.boolean(),
    headline: z.string(),
    logoAssetId: z.string().nullable(),
    imageAssetId: z.string().nullable(),
  }),
});

export function initialDraft(campaignPrefix: string, adGroupPrefix: string): SponsoredDisplayDraft {
  const timestamp = format(new Date(), "M/d/yyyy HH:mm:ss.SSS");
  return {
    campaignName: `${campaignPrefix} - ${timestamp}`,
    portfolioId: null,
    startDate: "",
    endDate: "",
    dailyBudget: "0",
    adGroupName: `${adGroupPrefix} - ${timestamp}`,
    optimizationGoal: "page_visits",
    targetingMode: "audience",
    audienceTab: "amazon",
    contextualTab: "categories",
    bidMode: "suggested",
    customBid: "0.75",
    products: [],
    productEnterInput: "",
    audienceTargets: [],
    contextualTargets: [],
    contextualAsinInput: "",
    audienceSearch: "",
    creative: {
      expanded: false,
      placement: "product_page_right",
      previewPage: 1,
      logoEnabled: false,
      headlineEnabled: false,
      imageEnabled: false,
      headline: "",
      logoAssetId: null,
      imageAssetId: null,
    },
  };
}

export function restoreDraft(
  storageKey: string,
  campaignPrefix: string,
  adGroupPrefix: string,
): SponsoredDisplayDraft {
  try {
    const stored = sessionStorage.getItem(storageKey);
    if (!stored) return initialDraft(campaignPrefix, adGroupPrefix);
    const parsed = SponsoredDisplayDraftSchema.safeParse(JSON.parse(stored));
    return parsed.success ? parsed.data : initialDraft(campaignPrefix, adGroupPrefix);
  } catch {
    return initialDraft(campaignPrefix, adGroupPrefix);
  }
}
